import React, { useRef, useState } from 'react';

interface Props {
  title: React.ReactNode;
  /** 右侧标题栏操作区（可选，如缩放/截图按钮） */
  headerExtra?: React.ReactNode;
  onClose: () => void;
  initialWidth?: string;   // CSS 宽度，如 'min(560px,95vw)'
  initialHeight?: string;
  minWidth?: number;
  minHeight?: number;
  children: React.ReactNode;
  /** 遮罩 z-index，默认 50 */
  zIndex?: number;
}

/**
 * 可拖动位置 + 可调整大小的面板外壳。
 * - 标题栏拖动移动（transform 平移，不随滚动）
 * - 右下角 CSS resize 调整大小
 * - 点遮罩或 ✕ 关闭；面板内点击不冒泡
 */
export const DraggableResizablePanel: React.FC<Props> = ({
  title,
  headerExtra,
  onClose,
  initialWidth = 'min(560px, 95vw)',
  initialHeight = 'min(82vh, 760px)',
  minWidth = 320,
  minHeight = 320,
  children,
  zIndex = 50,
}) => {
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const drag = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, bx: pos.x, by: pos.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPos({ x: drag.current.bx + (e.clientX - drag.current.sx), y: drag.current.by + (e.clientY - drag.current.sy) });
  };
  const onPointerUp = () => { drag.current = null; };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40"
      style={{ zIndex }}
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: initialWidth,
          height: initialHeight,
          minWidth,
          minHeight,
          maxWidth: '98vw',
          maxHeight: '92vh',
          resize: 'both',
          transform: `translate(${pos.x}px, ${pos.y}px)`,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* 标题栏：拖拽手柄 */}
        <div
          className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-gray-100 cursor-move select-none touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="min-w-0 flex items-center gap-2">{title}</div>
          <div className="flex items-center gap-2" onPointerDown={(e) => e.stopPropagation()}>
            {headerExtra}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};
